import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import { lookupsJson } from '@ourbranch/lookups';
import Field from 'core/components/form/form.v2';
import { Label } from 'core/components/label';
import { LabelTooltip } from 'core/components/label-tooltip';
import { getConstructionTypeExemptPropertyTypes } from 'common/helpers/farm-helpers';
import { RenovationFieldsForm } from 'common/components/farm/renovation-fields-form';

import useStyles from './mobile-home-form.styles';

// Form options constants
const mobileHomeQualityGradeOptions = lookupsJson.farm.mobileHomeQualityGrade;
const heatingSourceOptions = lookupsJson.farm.primaryHeatingSourceOptions;
const tieDownTypeOptions = lookupsJson.farm.tieDownTypes;
const mobileHomeConfigOptions = lookupsJson.farm.mobileHomeConfig;

// Mobile homes never use the "Tiny Home" (T) construction type — exclude it from the override list.
const constructionTypeOptions = lookupsJson.farm.dwellingConstructionType.filter((opt) => opt.id !== 'T');
const roofShapeOptions = lookupsJson.roofShape;
const roofTypeOptions = lookupsJson.farm.mobileHomeRoofType;
const numFamiliesInDwellingOptions = lookupsJson.farm.numFamiliesInDwellingOptions;

const MobileHomeForm = ({
  basePath,
  disableRiskForm,
  initialBuilding,
  initialConnectedHome,
  toastRCEWarning,
  state,
  riskType,
  setFieldValue
}) => {
  const classes = useStyles();
  const showConstructionType = !getConstructionTypeExemptPropertyTypes(state, riskType).includes('MH');

  // Set default value for ownership for new risks only
  useEffect(() => {
    if (setFieldValue && basePath) {
      // Only set default if value doesn't already exist (new risk)
      if (!initialBuilding?.ownershipStatus) {
        setFieldValue(`${basePath}.building.ownershipStatus`, 'P');
      }
    }
  }, [basePath, setFieldValue, initialBuilding?.ownershipStatus]);

  return (
    <>
      <Grid item xs={12}>
        <Label type="infoTitleSmall">Property</Label>
      </Grid>
      <Field
        xs={showConstructionType ? 4 : 6}
        name={`${basePath}.building.yearBuilt`}
        id={`${basePath}.building.yearBuilt`}
        type="numeric"
        label="Year built in"
        mode="light"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.yearBuilt) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={showConstructionType ? 4 : 6}
        type="numeric"
        id={`${basePath}.building.sqFt`}
        name={`${basePath}.building.sqFt`}
        mode="light"
        label="Total finished sqft"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.sqFt) {
            toastRCEWarning?.();
          }
        }}
      />
      {showConstructionType && (
        <Field
          xs={4}
          type="select"
          options={constructionTypeOptions}
          id={`${basePath}.building.constructionType`}
          name={`${basePath}.building.constructionType`}
          mode="light"
          label="Construction type"
          disabled={disableRiskForm}
          onChange={(val) => {
            if (val !== initialBuilding?.constructionType) {
              toastRCEWarning?.();
            }
          }}
        />
      )}
      <Field
        xs={6}
        type="select"
        options={mobileHomeQualityGradeOptions}
        id={`${basePath}.building.mobileHomeQualityGrade`}
        name={`${basePath}.building.mobileHomeQualityGrade`}
        mode="light"
        label="Home quality"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.mobileHomeQualityGrade) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={6}
        type="select"
        options={mobileHomeConfigOptions}
        id={`${basePath}.building.mobileHomeConfig`}
        name={`${basePath}.building.mobileHomeConfig`}
        mode="light"
        label="Mobile home config"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.mobileHomeConfig) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={6}
        type="select"
        options={numFamiliesInDwellingOptions}
        id={`${basePath}.building.numFamiliesInDwelling`}
        name={`${basePath}.building.numFamiliesInDwelling`}
        mode="light"
        label="Families in dwelling"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.numFamiliesInDwelling) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={4}
        type="numeric"
        id={`${basePath}.building.roofYear`}
        name={`${basePath}.building.roofYear`}
        mode="light"
        label="Roof year"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.roofYear) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={6}
        id={`${basePath}.building.roofType`}
        name={`${basePath}.building.roofType`}
        type="select"
        options={roofTypeOptions}
        mode="light"
        label="Roof type"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.roofType) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={6}
        id={`${basePath}.building.roofShape`}
        name={`${basePath}.building.roofShape`}
        type="select"
        options={roofShapeOptions}
        mode="light"
        label="Roof shape"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.roofShape) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={6}
        type="select"
        id={`${basePath}.building.tieDownType`}
        name={`${basePath}.building.tieDownType`}
        mode="light"
        label="Tie down type"
        options={tieDownTypeOptions}
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.tieDownType) {
            toastRCEWarning?.();
          }
        }}
      />
      <Grid item xs={12}>
        <LabelTooltip
          label="Solid Fuel-Burning Appliances"
          tooltip={{
            label: 'More Info',
            onHoverText:
              'Solid Fuel or wood heat examples include standard fireplace, fireplace with insert, indoor solid fuel or wood heat (including pellet stoves), outside solid fuel or wood heat. Barrel or Homemade stoves are Not Acceptable.'
          }}
          mode="light"
        >
          <Field
            type="numeric"
            id={`${basePath}.building.numSolidFuelBurningAppliances`}
            name={`${basePath}.building.numSolidFuelBurningAppliances`}
            mode="light"
            format={{
              allowNegative: false
            }}
            disabled={disableRiskForm}
            onChange={(val) => {
              if (val !== initialBuilding?.numSolidFuelBurningAppliances) {
                toastRCEWarning?.();
              }
            }}
          />
        </LabelTooltip>
      </Grid>
      <Field
        xs={3}
        id={`${basePath}.building.primaryHeatingSource`}
        name={`${basePath}.building.primaryHeatingSource`}
        type="select"
        options={heatingSourceOptions}
        mode="light"
        label="Primary heating"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.primaryHeatingSource) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={3}
        id={`${basePath}.building.secondaryHeatingSource`}
        name={`${basePath}.building.secondaryHeatingSource`}
        type="select"
        options={heatingSourceOptions}
        mode="light"
        label="Secondary heating"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.secondaryHeatingSource) {
            toastRCEWarning?.();
          }
        }}
      />
      <RenovationFieldsForm
        basePath={basePath}
        disabled={disableRiskForm}
        checkboxXs={6}
        onFieldChange={() => toastRCEWarning?.()}
      />
      <Field
        xs={6}
        id={`${basePath}.connectedHome.theftPreventionDeviceType`}
        name={`${basePath}.connectedHome.theftPreventionDeviceType`}
        type="checkbox"
        mode="light"
        label="Theft protective device"
        disabled={disableRiskForm}
        checkedValue="centralStation"
        uncheckedValue="none"
        onChange={(_e, checked) => {
          const initialIsChecked = initialConnectedHome?.theftPreventionDeviceType === 'centralStation';
          if (checked !== initialIsChecked) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={6}
        id={`${basePath}.connectedHome.fireProtectiveDeviceType`}
        name={`${basePath}.connectedHome.fireProtectiveDeviceType`}
        type="checkbox"
        mode="light"
        label="Fire protective device"
        disabled={disableRiskForm}
        checkedValue="centralStation"
        uncheckedValue="none"
        onChange={(_e, checked) => {
          const initialIsChecked = initialConnectedHome?.fireProtectiveDeviceType === 'centralStation';
          if (checked !== initialIsChecked) {
            toastRCEWarning?.();
          }
        }}
      />
      <Grid item xs={6} className={classes.checkboxGridItem}>
        <Field
          name={`${basePath}.building.enclosedMasonryFoundation`}
          id={`${basePath}.building.enclosedMasonryFoundation`}
          type="checkbox"
          label="Enclosed masonry foundation?"
          mode="light"
          disabled={disableRiskForm}
          className={classes.checkboxContainer}
          onChange={(val) => {
            if (val !== initialBuilding?.enclosedMasonryFoundation) {
              toastRCEWarning?.();
            }
          }}
        />
      </Grid>
      <Grid item xs={6} className={classes.checkboxGridItem}>
        <Field
          name={`${basePath}.building.fullSkirtingAroundBase`}
          id={`${basePath}.building.fullSkirtingAroundBase`}
          type="checkbox"
          label="Is there a full skirting around the base?"
          mode="light"
          disabled={disableRiskForm}
          className={classes.checkboxContainer}
          onChange={(val) => {
            if (val !== initialBuilding?.fullSkirtingAroundBase) {
              toastRCEWarning?.();
            }
          }}
        />
      </Grid>
      <Grid item xs={6} className={classes.checkboxGridItem}>
        <Field
          name={`${basePath}.connectedHome.internetMonitoring`}
          id={`${basePath}.connectedHome.internetMonitoring`}
          type="checkbox"
          label="Internet connected monitoring device(s)?"
          mode="light"
          disabled={disableRiskForm}
          className={classes.checkboxContainer}
          onChange={(val) => {
            if (val !== initialConnectedHome?.internetMonitoring) {
              toastRCEWarning?.();
            }
          }}
        />
      </Grid>
    </>
  );
};

MobileHomeForm.propTypes = {
  basePath: PropTypes.string.isRequired,
  disableRiskForm: PropTypes.bool,
  initialBuilding: PropTypes.object,
  initialConnectedHome: PropTypes.object,
  toastRCEWarning: PropTypes.func,
  state: PropTypes.string,
  riskType: PropTypes.string,
  setFieldValue: PropTypes.func
};

export default MobileHomeForm;
